import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { COURSE_ACCENT, COURSE_LABEL } from "@/data/lessons";
import { SCORES } from "@/data/scores";
import { STAGES } from "./curriculum";
import type { Score } from "./types";

/**
 * The course front page: every piece at a glance, grouped by stage.
 *
 * This is where a student lands from the LMS, so it has to answer three
 * questions immediately — what is in here, where do I start, and what has this
 * got that a book of songs hasn't.
 */
export default function SightReadingCourse() {
  const accent = COURSE_ACCENT["sight-reading"];
  const [filter, setFilter] = useState<"all" | "book" | "new">("all");

  const bySlug = useMemo(
    () => Object.fromEntries(SCORES.map((s) => [s.slug, s])) as Record<string, Score>,
    [],
  );
  const totals = useMemo(() => ({
    pieces: SCORES.length,
    bars: SCORES.reduce((a, s) => a + s.bars, 0),
    notes: SCORES.reduce((a, s) => a + s.notes.filter((n) => !n.rest).length, 0),
    fresh: SCORES.filter((s) => s.newForThisEdition).length,
  }), []);

  const show = (s: Score) =>
    filter === "all" || (filter === "new" ? !!s.newForThisEdition : !s.newForThisEdition);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header
        course={COURSE_LABEL["sight-reading"]}
        lesson="All thirty pieces"
        courseSlug="sight-reading"
      />
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-10">
        <p
          className="font-mono text-xs uppercase tracking-[0.18em]"
          style={{ color: accent.hex }}
        >
          {COURSE_LABEL["sight-reading"]}
        </p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
          Most piano books teach you songs.
          <br />
          This one teaches you to read anything.
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-zinc-400">
          Thirty pieces, from Bach and Mozart through to Jason's own — each one
          with the notation playable on the page. And because you can only ever
          sight-read a piece once, every piece can also generate fresh music you
          have never seen, graded to match it.
        </p>

        <dl className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
          {[
            ["Pieces", totals.pieces],
            ["Bars", totals.bars],
            ["Notes", totals.notes.toLocaleString()],
            ["Stages", STAGES.length],
          ].map(([label, value]) => (
            <div key={label as string}>
              <dt className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                {label}
              </dt>
              <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-10 flex flex-wrap gap-2">
          {([
            ["all", `All ${totals.pieces}`],
            ["book", `From the book (${totals.pieces - totals.fresh})`],
            ["new", `New for this edition (${totals.fresh})`],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                filter === key
                  ? "text-zinc-950"
                  : "border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }`}
              style={filter === key ? { background: accent.hex } : undefined}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-10 space-y-12">
          {STAGES.map((stage) => {
            const pieces = stage.pieces.map((p) => bySlug[p]).filter(Boolean).filter(show);
            if (!pieces.length) return null;
            return (
              <section key={stage.n}>
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span
                    className="font-mono text-sm tabular-nums"
                    style={{ color: accent.hex }}
                  >
                    {String(stage.n).padStart(2, "0")}
                  </span>
                  <h2 className="text-xl font-semibold">{stage.title}</h2>
                </div>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-zinc-400">
                  {stage.aim}
                </p>
                {stage.n === 3 && (
                  <p className="mt-3 max-w-2xl rounded-lg border-l-2 bg-zinc-900/60 p-3 text-sm leading-relaxed text-zinc-300"
                     style={{ borderColor: accent.hex }}>
                    New for this edition. The printed book introduced the bass clef
                    already inside a two-hand grand staff — a new clef and hands
                    together in the same breath, which is where most beginners
                    stall. These three teach it on its own first.
                  </p>
                )}

                <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {pieces.map((score) => (
                    <li key={score.slug}>
                      <Link
                        to={`/lesson/sight-reading-${score.n}`}
                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/40 transition hover:border-zinc-600"
                      >
                        {score.art ? (
                          <img
                            src={score.art}
                            alt=""
                            className="h-32 w-full bg-white object-contain"
                            loading="lazy"
                          />
                        ) : score.background ? (
                          /* Gap-filler pieces have no book plate; their
                             generated 16:9 scene is the card image, with the
                             edition badge kept as a quiet overlay so that
                             fact is not lost with the grey box. */
                          <div className="relative">
                            <img
                              src={score.background}
                              alt=""
                              className="h-32 w-full object-cover"
                              loading="lazy"
                            />
                            {score.newForThisEdition && (
                              <span className="absolute bottom-2 left-2 rounded bg-zinc-950/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                                new for this edition
                              </span>
                            )}
                          </div>
                        ) : (
                          <div
                            className="flex h-32 w-full items-center justify-center"
                            style={{ background: `${accent.hex}12` }}
                          >
                            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                              new for this edition
                            </span>
                          </div>
                        )}
                        <div className="flex flex-1 flex-col p-4">
                          <h3 className="font-semibold leading-snug">
                            {score.title}
                          </h3>
                          <p className="mt-0.5 text-sm text-zinc-400">
                            {score.composer}
                          </p>
                          <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                            {score.key.replace(/b$/, "♭").replace(/#$/, "♯")} · {score.time[0]}/{score.time[1]} ·{" "}
                            {score.bars} bars ·{" "}
                            {score.staves > 1 ? "both hands" : score.clefs[0]}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
